import {
  FormControl,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  Text,
  ModalBody,
  ModalFooter,
  Flex,
  Checkbox,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import {
  Material,
  UpdateMaterialRequest,
  materialsApi,
} from '@texas/api/endpoints/metadata/materialsApi';
import { FormCounter } from '@texas/components/shared/form/FormCounter';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { formatLabel } from '@texas/components/shared/composition/material/shared';

interface Props extends SharedDisclosureProps {
  onUpdated: (material: Material) => void;
  materialId: number | null;
}

export function EditMaterial({
  onUpdated,
  materialId,
  onClose,
  isOpen,
}: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      variant="entityDirectory"
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>{t('general.edit')}</ModalHeader>
        {materialId && (
          <Form
            materialId={materialId}
            onClose={onClose}
            onUpdated={onUpdated}
          />
        )}
      </ModalContent>
    </Modal>
  );
}

function Form({
  materialId,
  onClose,
  onUpdated,
}: {
  materialId: number;
  onClose: () => void;
  onUpdated: (material: Material) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const { request: updateRequest, error } = useApiRequest(materialsApi.update);

  const {
    data: materials,
    refetch: refetchMaterials,
    loading: materialsLoading,
  } = useApiResource(materialsApi.getAll);

  const { data: material, refetch: refetchMaterial } = useApiResource(
    materialsApi.get,
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<UpdateMaterialRequest>();
  const watchCombinations = watch('childMaterialIds');

  useEffect(() => {
    refetchMaterial(materialId);
    refetchMaterials();
  }, [materialId, refetchMaterial, refetchMaterials]);

  useEffect(() => {
    if (!material) return;
    reset({
      name: material.name,
      description: material.description,
      displayOrder: material.displayOrder,
      articleTextField: material.articleTextField,
      certificationTextField: material.certificationTextField,
      childMaterialIds: material.combinationOf.map((x) => x.id),
      lockArticleAfterSync: material.lockArticleAfterSync,
    });
  }, [material, reset]);

  const onSubmit = async (data: UpdateMaterialRequest) =>
    await request(updateRequest, [materialId, data], (data: Material) => {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      onUpdated(data);
      onClose();
    });

  return (
    <>
      {error && <ErrorDetails error={error} />}
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <ModalBody>
          <Flex flexDir="column" gap={4}>
            <FormControl isRequired={true} isInvalid={!!errors.name}>
              <TexasFormLabel>{t('general.name')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('name', {
                  required: true,
                  maxLength: {
                    value: 35,
                    message: t('errors.maxLength', { count: 35 }),
                  },
                })}
                placeholder={t('general.name')}
              />
              <HookFormCounter control={control} name="name" maxLength={35} />
              <ErrorLabel text={errors.name?.message} />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.description}>
              <TexasFormLabel>{t('general.description')}</TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('description', {
                  required: true,
                  maxLength: {
                    value: 35,
                    message: t('errors.maxLength', { count: 35 }),
                  },
                })}
                placeholder={t('general.description')}
              />
              <HookFormCounter
                control={control}
                name="description"
                maxLength={35}
              />
              <ErrorLabel text={errors.description?.message} />
            </FormControl>
            <FormControl
              isRequired={true}
              isInvalid={!!errors.articleTextField}
            >
              <TexasFormLabel>
                {t('configuration.articleTextField')}
              </TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('articleTextField', {
                  required: true,
                  maxLength: {
                    value: 20,
                    message: t('errors.maxLength', { count: 20 }),
                  },
                })}
                placeholder={t('configuration.articleTextField')}
              />
              <ErrorLabel text={errors.articleTextField?.message} />
              <FormCounter
                length={watch('articleTextField', '').length}
                maxLength={20}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.certificationTextField}>
              <TexasFormLabel>
                {t('configuration.certificationTextField')}
              </TexasFormLabel>
              <Input
                variant="entityDirectory"
                {...register('certificationTextField', {
                  maxLength: {
                    value: 20,
                    message: t('errors.maxLength', { count: 20 }),
                  },
                })}
                placeholder={t('configuration.certificationTextField')}
              />
              <ErrorLabel text={errors.certificationTextField?.message} />
              <FormCounter
                length={watch('certificationTextField', '').length}
                maxLength={20}
              />
            </FormControl>
            <FormControl isRequired={true} isInvalid={!!errors.displayOrder}>
              <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
              <Input
                type="number"
                variant="entityDirectory"
                {...register('displayOrder', {
                  required: true,
                  valueAsNumber: true,
                })}
                placeholder={t('branch.displayOrder')}
              />
              <ErrorLabel text={errors.displayOrder?.message} />
            </FormControl>
            <FormControl>
              <TexasFormLabel>
                {t('configuration.lockMtrlAfterSync')}
              </TexasFormLabel>
              <Controller
                name="lockArticleAfterSync"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox onChange={onChange} isChecked={value} />
                )}
              />
            </FormControl>
            <FormControl>
              <TexasFormLabel>
                {t('configuration.combinationOf')}
              </TexasFormLabel>
              <Controller
                name="childMaterialIds"
                control={control}
                render={({ field }) => (
                  <TexasSelect
                    {...field}
                    isLoading={materialsLoading}
                    isClearable={true}
                    value={field.value?.map<ReactSelectOption>((x) => ({
                      label: formatLabel(materials?.find((p) => p.id === x)),
                      value: x,
                    }))}
                    isMulti={true}
                    onChange={(e) => field.onChange(e.map((x) => x.value))}
                    options={
                      materials?.map<ReactSelectOption>((x) => ({
                        value: x.id,
                        label: formatLabel(x),
                      })) ?? []
                    }
                    closeMenuOnSelect={false}
                  />
                )}
              />
              <Text variant="small" pt={1}>
                {t('configuration.mixedDesc')}
              </Text>
            </FormControl>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <SubmitButton
            disabled={watchCombinations?.length == 1}
            loading={isSubmitting}
          >
            {watchCombinations?.length == 0
              ? t('configuration.updateMaterial')
              : t('configuration.updateMixedMaterial')}
          </SubmitButton>
        </ModalFooter>
      </form>
    </>
  );
}
